import { useState } from 'react';
import { brandPhilosophy, homeFAQ } from '@/mocks/home';
import { clamp, TRANSITION, COLORS } from './progress';

interface InfoSceneProps {
  progress: number;
}

export default function InfoScene({ progress }: InfoSceneProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const p = clamp(progress, 0, 1);
  const enter = clamp(p * 3, 0, 1);
  const exit = 1 - clamp((p - 0.82) * 5.5, 0, 1);
  const opacity = Math.min(enter, exit);
  const translateY = (1 - enter) * 60;

  return (
    <div
      className="absolute inset-0 z-20 flex flex-col items-center justify-center px-5 md:px-8 pt-20 md:pt-24 pb-10 pointer-events-none"
      style={{ opacity, transform: `translateY(${translateY}px)`, transition: TRANSITION, willChange: 'transform, opacity' }}
    >
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
        {/* 左：選書理念 */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-white/40" />
            <span className="font-manrope text-white/70 text-[10px] uppercase tracking-[0.35em]">
              Vol. 04 — 選書理念
            </span>
          </div>
          <h2 className="font-semibold text-white text-2xl md:text-3xl mb-6">
            策點文化如何選書與策展？
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {brandPhilosophy.map((item, i) => (
              <div
                key={item.id}
                className="p-4 md:p-5 border"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderColor: COLORS.line }}
              >
                <div className="flex items-center gap-2.5 mb-3">
                  <span
                    className="w-7 h-7 flex items-center justify-center rounded-md"
                    style={{ backgroundColor: 'rgba(224,177,94,0.16)', color: COLORS.amber }}
                  >
                    <i className={`${item.icon} text-sm`}></i>
                  </span>
                  <span className="font-michroma text-[10px] text-white/35 tracking-widest">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="font-semibold text-white text-sm mb-1.5">{item.title}</h3>
                <p className="font-manrope text-[11px] md:text-xs text-white/55 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* 右：常見問答 */}
        <div className="pointer-events-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-white/40" />
            <span className="font-manrope text-white/70 text-[10px] uppercase tracking-[0.35em]">
              FAQ — 常見問答
            </span>
          </div>
          <h2 className="font-semibold text-white text-2xl md:text-3xl mb-6">
            關於策點文化的常見問題
          </h2>
          <div className="flex flex-col gap-2">
            {homeFAQ.map((item, idx) => {
              const isOpen = openIndex === idx;
              return (
                <div key={idx} className="border overflow-hidden" style={{ borderColor: COLORS.line, backgroundColor: 'rgba(255,255,255,0.03)' }}>
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
                  >
                    <span className="font-manrope text-xs md:text-sm font-medium text-white/85 pr-3">
                      {item.q}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-base flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                      style={{ color: COLORS.cyan }}
                    ></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-72' : 'max-h-0'}`}>
                    <p className="px-4 pb-3 font-manrope text-[11px] md:text-xs text-white/55 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}